import Modal from "@material-ui/core/Modal";
import { Text } from "components/Typography/text.styled";
import { FontFamily, FontSize } from "config/font";
import { Filter, FilterType, GroupType } from "model/Calendar";
import {
  CompanyGig,
  CompanyGigShift,
  CompanyGigSkills,
  GigType,
  PaymentMethod,
} from "model/Gig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCalendarStore } from "web-apps/company/stores/calendarStore/calendarStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { AlertModal } from "../../general/alertModal/alertModal.component";
import { ButtonComponent } from "../../general/button/button.component";
import { DividerWithText } from "../../general/dividerWithText/dividerWithText.component";
import { TopModal } from "../../general/topModal/topModal.component";
import { Worker } from "../../organizationalStructure/chooseWorkersModal.component";
import { TabsStyled, TabStyled } from "../../tab/tab.styled";
import { CloseGigModal } from "../gigList/closeGigModal.component";
import { ModalBody } from "./createGigModal.styled";
import { RecruitGigForm } from "./recruitGigForm.component";
import { ScheduleGigForm } from "./scheduleGigForm.component";
import { ShiftForm } from "./shiftForm.component";
import { ShiftList } from "./shiftList.component";
import { TextareaWithLabel } from "../../general/textAreaWithLabel/textareaWithLabel.component";

type Props = {
  setShowGigFormModal: React.Dispatch<React.SetStateAction<boolean>>;
  showGigFormModal: boolean;
  gigData?: CompanyGig;
  onGigUpdated?: (gig: CompanyGig) => void;
  shifts?: CompanyGigShift[];
  type?: GigType;
};

export type RecruitFormData = {
  skills: CompanyGigSkills[];
  amountOfWorkers: number;
  paymentType: PaymentMethod;
  workGroupId?: number;
  role: string;
  maxRate: number;
  minRate: number;
};

export type FormData = {
  shifts: CompanyGigShift[];
  description: string;
  type: GigType;
};

type Tab = "Schedule" | "Recruit";

const RECRUIT_TARGET_TYPE: GigType = GigType.WORKPOOL;

export const ShiftFormHHMMRegex = /^([01]?[0-9]|2[0-3]|24):[0-5][0-9]$/;

export const GigFormModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [_state, _actions, alertDispatch] = useAlertStore();
  const [selectedWorkers, setSelectedWorkers] = useState<Worker[]>([]);
  const [shiftListOpen, setShiftListOpen] = useState(true);
  const [overlapping, setOverlapping] = useState<
    { worker: any; shifts: CompanyGigShift[] }[]
  >([]);
  const [showOverlappingError, setShowOverlappingError] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showChangeTabAlert, setShowChangeTabAlert] = useState(false);
  const [changeTo, setChangeTo] = useState<GigType>(GigType.SOLO);
  const [, calendarActions] = useCalendarStore();
  const [companyState, ,] = useCompanyStore();
  const [filter] = useState<Filter[]>([
    {
      filterId: -1,
      groupType: GroupType.NONE,
      filterType: FilterType.NONE,
    },
  ]);
  const [showAlert, setShowAlert] = useState(false);

  const isPublicOrTeamType = useMemo(() => {
    const t = props.gigData?.type;
    return (
      t === GigType.PUBLIC ||
      t === GigType.HIDDEN ||
      t === GigType.STAFFING ||
      t === GigType.WORKPOOL
    );
  }, [props.gigData?.type]);

  const havePublicModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowPublicGigModule",
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const haveTeamModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowTeamGigModule",
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const canRecruit = havePublicModule || haveTeamModule;

  const initialTab: Tab =
    canRecruit && isPublicOrTeamType ? "Recruit" : "Schedule";
  const [tab, setTab] = useState<Tab>(initialTab);

  const initialFormData = useMemo(
    () => ({
      description: props.gigData?.description ?? "",
      shifts:
        props.gigData?.shifts ?? props.shifts ?? ([] as CompanyGigShift[]),
      type: props.gigData?.type ?? props.type ?? GigType.SOLO,
    }),
    [props.gigData, props.shifts, props.type],
  );

  const initialRecruitFormData = useMemo(
    () => ({
      skills: props.gigData?.skills ?? [],
      amountOfWorkers: props.gigData?.amountOfWorkers ?? 1,
      paymentType: props.gigData?.paymentType ?? PaymentMethod.NONE,
      workGroupId: props.gigData?.structure.workGroupId,
      role: props.gigData?.role ?? "",
      maxRate: props.gigData?.maxRate ?? 170,
      minRate: props.gigData?.minRate ?? 140,
    }),
    [props.gigData],
  );

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [recruitFormData, setRecruitFormData] = useState<RecruitFormData>(
    initialRecruitFormData,
  );

  useEffect(() => {
    if (!props.showGigFormModal) return;
    setFormData(initialFormData);
    setRecruitFormData(initialRecruitFormData);
    setSelectedWorkers([]);
    setTab(initialTab);
    setOverlapping([]);
    setShowOverlappingError(false);
  }, [
    props.showGigFormModal,
    initialFormData,
    initialRecruitFormData,
    initialTab,
  ]);

  useEffect(() => {
    if (!canRecruit && tab === "Recruit") setTab("Schedule");
    if (!props.gigData && !canRecruit && formData.type !== GigType.SOLO) {
      setFormData((curr) => ({ ...curr, type: GigType.SOLO }));
    }
  }, [canRecruit, tab, props.gigData, formData.type, setFormData]);

  const deepEqual = useCallback(
    (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b),
    [],
  );

  const isScheduleDirty = useMemo(
    () => !deepEqual(formData, initialFormData) || selectedWorkers.length > 0,
    [deepEqual, formData, initialFormData, selectedWorkers.length],
  );

  const isRecruitDirty = useMemo(
    () => !deepEqual(recruitFormData, initialRecruitFormData),
    [deepEqual, recruitFormData, initialRecruitFormData],
  );

  const closeModal = () => {
    if (isLoading) return;
    props.setShowGigFormModal(false);
    setFormData(initialFormData);
    setRecruitFormData(initialRecruitFormData);
    setSelectedWorkers([]);
    setOverlapping([]);
    setShowOverlappingError(false);
    setShowAlert(false);
  };

  const handleCreateGig = async (parentGigId?: number) => {
    const companyId = companyState.company?.id;
    if (!companyId) return;

    setIsLoading(true);

    const items =
      formData.type === GigType.PUBLIC
        ? [null]
        : formData.type === GigType.SOLO ||
            formData.type === GigType.AUTOGENERATED
          ? (selectedWorkers ?? [])
          : [null];

    const buildPayload = (w: any) => {
      switch (formData.type) {
        case GigType.PUBLIC:
          return { ...recruitFormData, ...formData };
        case GigType.SOLO:
        case GigType.AUTOGENERATED:
          return {
            ...formData,
            parentGigId,
            ...(w && {
              workerId: w.workerId ?? w.id,
              workGroupId: w.selectedTeamId ?? undefined,
            }),
          };
        default:
          return {
            ...formData,
            amountOfWorkers: recruitFormData.amountOfWorkers,
            role: recruitFormData.role,
            workGroupId: recruitFormData.workGroupId,
          };
      }
    };

    try {
      const results = await Promise.all(
        items.map((w) => {
          let payload = buildPayload(w);
          payload.customerProduct = w?.selectedCustomerProduct;
          return Api().company.gig.createGig(companyId, payload);
        }),
      );

      updateContexts();
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "",
          title: t("CreateShift.Created"),
          message: t("CreateShift.YourGigsHaveBeenCreated"),
        },
      });
      props.setShowGigFormModal(false);
    } catch (err) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getNewShifts = () =>
    (formData.shifts ?? []).filter((s: any) => s == null || s.id == null);

  type OverlapMode = "create" | "update";

  const checkOverlapping = (mode: OverlapMode) => {
    if (!companyState.company?.id) {
      if (mode === "update") {
        handleUpdateGig();
      }
      return;
    }

    const newShifts = getNewShifts();
    const payloadShifts: CompanyGigShift[] = newShifts;

    if (!payloadShifts.length || selectedWorkers.length === 0) {
      setOverlapping([]);
      setShowOverlappingError(false);

      if (mode === "update") {
        handleUpdateGig();
      }

      return;
    }

    const promises = selectedWorkers.map((worker) => {
      const data = {
        workerId: worker.workerId ?? worker.id,
        workGroupId: worker.selectedTeamId,
        description: formData.description,
        shifts: payloadShifts,
        type: GigType.SOLO,
      };

      return Api()
        .company.gig.getOverlappingGig(companyState.company!.id, data)
        .then((res) => ({
          worker,
          shifts: (res.data?.shifts ?? []) as CompanyGigShift[],
        }))
        .catch(() => ({
          worker,
          shifts: [] as CompanyGigShift[],
        }));
    });

    Promise.all(promises).then((results) => {
      const overlappingResults = results.filter(
        (r) => r.shifts && r.shifts.length > 0,
      );

      if (overlappingResults.length > 0) {
        setOverlapping(overlappingResults);
        setShowOverlappingError(true);
        setShiftListOpen(true);
      } else {
        setOverlapping([]);
        setShowOverlappingError(false);

        if (mode === "update") {
          handleUpdateGig();
        }
      }
    });
  };

  useEffect(() => {
    checkOverlapping("create");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkers, formData.shifts]);

  const validate = useMemo(() => {
    const shiftsOk = (formData.shifts?.length ?? 0) > 0;
    const descOk = (formData.description?.length ?? 0) <= 2056;
    if (!shiftsOk || !descOk) return false;

    const hasWorkersWithTeams =
      selectedWorkers.length > 0 &&
      selectedWorkers.every((w) => w.selectedTeamId != null);

    const roleOk =
      typeof recruitFormData.role === "string" &&
      recruitFormData.role.trim().length > 0 &&
      recruitFormData.role.length <= 24;

    const workGroupOk = recruitFormData.workGroupId != null;

    const min = Number(recruitFormData.minRate);
    const max = Number(recruitFormData.maxRate);
    const ratesOk = Number.isFinite(min) && Number.isFinite(max) && max >= min;

    switch (formData.type) {
      case GigType.SOLO:
      case GigType.AUTOGENERATED:
        return hasWorkersWithTeams && !showOverlappingError;

      case GigType.PUBLIC:
        return roleOk && workGroupOk && ratesOk;

      default:
        return roleOk && workGroupOk;
    }
  }, [formData, selectedWorkers, recruitFormData, showOverlappingError]);

  const handleUpdateGig = () => {
    if (!companyState.company?.id || !props.gigData) return;

    setIsLoading(true);

    const data =
      formData.type == GigType.SOLO || formData.type == GigType.AUTOGENERATED
        ? { ...formData }
        : formData.type == GigType.PUBLIC
          ? {
              ...recruitFormData,
              ...formData,
            }
          : {
              ...formData,
              amountOfWorkers: recruitFormData.amountOfWorkers,
              role: recruitFormData.role,
              workGroupId: recruitFormData.workGroupId,
            };

    Api()
      .company.gig.updateGig(companyState.company.id, data, props.gigData?.id)
      .then(() => {
        updateContexts();
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("CreateShift.Updated"),
            message: t("CreateShift.YourGigsHaveBeenUpdated"),
          },
        });
        closeModal();
        if (props.gigData) props.onGigUpdated?.(props.gigData);
      })
      .catch(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("Alerts.SomethingWentWrong"),
          },
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const updateContexts = () => {
    calendarActions.updateWeeklyCalendarContext(
      t,
      companyState,
      "UPDATE_OVERVIEW_WEEK_CALENDAR_V2",
      filter,
    );

    calendarActions.updateWeeklyCalendarContext(
      t,
      companyState,
      "UPDATE_WEEKLY_CALENDAR_V2",
      filter,
    );
  };

  const handleCreateParentGig = () => {
    if (!companyState.company?.id) return;
    Api()
      .company.gig.createParentGig(companyState.company.id)
      .then((res) => {
        handleCreateGig(res.data);
      })
      .catch(() => {
        console.log("error creating parent gig");
        setIsLoading(false);
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("Alerts.SomethingWentWrong"),
          },
        });
        return null;
      });
  };

  const handleChangeTab = (type: GigType) => {
    setFormData({ ...initialFormData, type });
    setRecruitFormData(initialRecruitFormData);
    if (type !== GigType.SOLO) {
      setSelectedWorkers([]);
    }

    setTab(type === GigType.SOLO ? "Schedule" : "Recruit");
    setShowChangeTabAlert(false);
  };

  const requestTabChange = (to: Tab) => {
    if (isLoading) return;
    if (to === "Recruit" && !canRecruit) return;

    if (to === "Schedule") {
      if (tab === "Recruit" && isRecruitDirty) {
        setChangeTo(GigType.SOLO);
        setShowChangeTabAlert(true);
      } else {
        setFormData((curr) => ({ ...curr, type: GigType.SOLO }));
        setTab("Schedule");
      }
    } else {
      if (tab === "Schedule" && isScheduleDirty) {
        setChangeTo(RECRUIT_TARGET_TYPE);
        setShowChangeTabAlert(true);
      } else {
        setFormData((curr) => ({ ...curr, type: RECRUIT_TARGET_TYPE }));
        setSelectedWorkers([]);
        setTab("Recruit");
      }
    }
  };

  const handleRequestClose = useCallback(
    (_e?: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        if (isScheduleDirty || isRecruitDirty) {
          setShowAlert(true);
        } else {
          closeModal();
        }
        return;
      }

      if (reason === "escapeKeyDown") {
        if (isScheduleDirty || isRecruitDirty) setShowAlert(true);
        else closeModal();
        return;
      }
    },
    [isScheduleDirty, isRecruitDirty],
  );

  return (
    <>
      <Modal open={props.showGigFormModal} onClose={handleRequestClose}>
        <ModalBody aria-busy={isLoading}>
          <TopModal
            title={
              !props.gigData
                ? t("CreateShift.CreateShift")
                : t("CreateShift.EditShift")
            }
            setClose={closeModal}
          />

          {!props.gigData && canRecruit && (
            <TabsStyled style={{ marginBottom: 40 }}>
              <TabStyled
                active={tab === "Schedule"}
                onClick={() =>
                  !isLoading ? requestTabChange("Schedule") : null
                }
              >
                <Text
                  fontFamily={FontFamily.MontserratRegular}
                  fontSize={FontSize.Standard}
                  style={{ marginBottom: 10 }}
                >
                  {t("CreateShift.Schedule")}
                </Text>
              </TabStyled>

              <TabStyled
                active={tab === "Recruit"}
                onClick={() =>
                  !isLoading ? requestTabChange("Recruit") : null
                }
              >
                <Text
                  fontFamily={FontFamily.MontserratRegular}
                  fontSize={FontSize.Standard}
                  style={{ marginBottom: 10 }}
                >
                  {t("CreateShift.Recruit")}
                </Text>
              </TabStyled>
            </TabsStyled>
          )}

          {tab == "Recruit" && canRecruit && (
            <RecruitGigForm
              shifts={formData.shifts}
              recruitFormData={recruitFormData}
              setRecruitFormData={setRecruitFormData}
              setFormData={setFormData}
              formData={formData}
              gigData={props.gigData}
              havePublicModule={havePublicModule}
              haveTeamModule={haveTeamModule}
            />
          )}

          {tab == "Schedule" && (
            <ScheduleGigForm
              setSelectedWorkers={setSelectedWorkers}
              selectedWorkers={selectedWorkers}
              gigData={props.gigData}
              shifts={formData.shifts}
            />
          )}

          <DividerWithText text={t("CreateShift.AddShift")} />
          {!props.shifts && (
            <ShiftForm
              setFormData={setFormData}
              selectedWorkers={selectedWorkers}
            />
          )}
          <ShiftList
            setShiftListOpen={setShiftListOpen}
            shiftListOpen={shiftListOpen}
            shifts={formData.shifts}
            setFormData={setFormData}
            formData={formData}
            overlapping={overlapping}
            showOverlappingError={showOverlappingError}
          />
          <DividerWithText text={t("CreateShift.Other")} />
          <TextareaWithLabel
            label={t("CreateShift.Description")}
            value={formData.description}
            onChange={(e) => {
              setFormData({
                ...formData,
                description: e.target.value,
              });
            }}
            placeholder={t("CreateShift.DescriptionInfo")}
            maxLength={500}
            width={800}
            minRows={4}
            marginBottom={20}
            disabled={isLoading}
          />
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <ButtonComponent
              size={"small"}
              onClick={() => {
                if (props.gigData) {
                  if (tab === "Schedule") {
                    checkOverlapping("update");
                  } else {
                    handleUpdateGig();
                  }
                } else {
                  if (tab === "Schedule") {
                    if (selectedWorkers.length >= 2) {
                      handleCreateParentGig();
                    } else {
                      handleCreateGig();
                    }
                  } else {
                    handleCreateGig();
                  }
                }
              }}
              action
              marginBottom={20}
              width={200}
              disabled={isLoading || !validate}
            >
              {!props.gigData
                ? t("CreateShift.CreateGig")
                : t("CreateShift.UpdateShift")}
            </ButtonComponent>
          </div>
        </ModalBody>
      </Modal>

      {showChangeTabAlert && (
        <AlertModal
          isOpen={showChangeTabAlert}
          title={t(`CreateShift.ShowChangeTabAlertTitle`)}
          message={t(`CreateShift.ShowChangeTabAlertInfo`)}
          onConfirm={() => handleChangeTab(changeTo)}
          onCancel={() => setShowChangeTabAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}

      {deleteModalOpen && props.gigData && (
        <CloseGigModal
          modalOpen={deleteModalOpen}
          setModalOpen={setDeleteModalOpen}
          gigData={props.gigData}
          onGigClosed={() => updateContexts()}
        />
      )}
      {showAlert && (
        <AlertModal
          isOpen={showAlert}
          title={t(`OrganizationalStructure.WorkerAlertTitle`)}
          message={t(`OrganizationalStructure.WorkerAlertInfo`)}
          onConfirm={() => closeModal()}
          onCancel={() => setShowAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}
    </>
  );
};
